import { ApolloClient, DocumentNode, TypedDocumentNode } from '@apollo/client';

import { FlagSource, FlagsSnapshot } from '@occc/data-flags';

export function createApolloFlagSource<TData>(
  client: ApolloClient<unknown>,
  query: DocumentNode | TypedDocumentNode<TData>,
  selectFlags: (data: TData | undefined) => FlagsSnapshot,
): FlagSource {
  let currentSnapshot = selectFlags(
    (client.readQuery<TData>({ query }) as TData | null) ?? undefined,
  );

  // cache-only: the flag source passively observes cache updates from the
  // main user query and local resolver writes. It never triggers network requests.
  const observable = client.watchQuery<TData>({
    query,
    fetchPolicy: 'cache-only',
  });

  return {
    subscribe: (listener: () => void) => {
      const subscription = observable.subscribe((result) => {
        const nextSnapshot = selectFlags(result.data as TData | undefined);

        if (!shallowEqual(currentSnapshot, nextSnapshot)) {
          currentSnapshot = nextSnapshot;
          listener();
        }
      });

      return () => subscription.unsubscribe();
    },
    getSnapshot: () => currentSnapshot,
  };
}

function shallowEqual(a: FlagsSnapshot, b: FlagsSnapshot): boolean {
  if (a === b) return true;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }

  return true;
}
